import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { Subject } from 'rxjs/internal/Subject';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';

import { setTheme } from 'ngx-bootstrap/utils';

import {
    AFormat,
    Box,
    BoxLevel,
    CardTemplate,
    Character,
    ExExercise,
    KairosBonus,
    KairosBonusSettings,
    MinifiedBox,
    Privilege,
    ShortDataLesson,
    SmartSprint,
    Currency,
    Profile
} from '@df/interfaces';
import { SubjectFileModel } from '@app/_interfaces/SubjectFileModel';

import { ExExerciseService } from '@app/core/services/ex_exercise.service';
import { LessonService } from '@app/teacher/services/lesson.service';
import { PrivilegeService } from '@app/core/services/privilege.service';
import { TemplateService } from '@app/core/services/template.service';
import { TaskService } from '@app/core/services/task.service';

import { LESSON_CANCELED, LESSON_CREATED, MAIN_DOMAIN } from '@app/app.constants';
import { armIcon } from '@app/app.constants';

import { SelectFilesComponent } from '@app/shared/components/select-files-popup/select-files.component';
import { MatDialog } from '@angular/material/dialog';
import { cloneDeep } from 'lodash';
import { declensionOfNumerals } from '@app/_utils/helpers';
import { AccountService } from '@app/teacher/services/account.service';

@Component({
    selector: 'app-box-edit',
    templateUrl: './box-edit.component.html',
    styleUrls: ['./box-edit.component.scss']
})
export class BoxEditComponent implements OnInit, OnDestroy {
    mainDomain = MAIN_DOMAIN;
    lesson: ShortDataLesson;
    lessonDate: Date;
    box: Box;
    levels: Array<BoxLevel>;
    character: Character;
    characters: Array<Character> = [];
    cardTemplate: CardTemplate = null;
    boxFilesIDs: number[] = [];
    initialSubtopics: Array<SubjectFileModel> = [];
    boxEditForm: FormGroup;
    isLoaded = false;
    subjectFiles: Array<SubjectFileModel>;
    isKairosBonusActivated: boolean = true;
    hasKairosBonus: boolean;
    kairosBonus: KairosBonus;
    kairosBonusSettings: KairosBonusSettings;
    exExercise: ExExercise;
    isExExerciseActive = false;
    isSmartSprintOpen = false;
    isHabirintBox = false;
    withRiskBox = false;
    currentCurrency: Currency;
    declensionOfNumerals = declensionOfNumerals;

    privilegeSettings: {
        id: number;
        character: Character;
        privilege: Privilege;
        boxes: any;
    }[] = [];
    isAccedEdit = false;
    activatedPrivileges = [];
    activedSmartSprints = [];
    boxLevel: number;
    profile: Profile;
    groupRights = {
        create_lesson: {
            index_title: 5,
            is_access: false
        },
        edit_other_lesson: {
            index_title: 3,
            is_access: false
        },
        change_status_other_lesson: {
            index_title: 6,
            is_access: false
        },
        copy_other_lesson: {
            index_title: 4,
            is_access: false
        },
        delete_other_lesson: {
            index_title: 2,
            is_access: false
        },
        edit_tamplates: {
            index_title: 7,
            is_access: false
        }
    };

    private destroy$: Subject<boolean> = new Subject<boolean>();

    @Output() chooseFileModalBox = new EventEmitter<Box>();
    @ViewChild('filesModal', { static: true }) filesModal: ElementRef;

    paper_formats = [
        { value: AFormat.A6_FORMAT, name: 'А6' }, // ( 1/4 А4 ) 4 cards
        { value: AFormat.A5_FORMAT, name: 'А5' }, // ( 1/2 А4 ) 2 cards
        { value: AFormat.A7_FORMAT, name: 'А7' }, // ( 1/8 А4 ) 8 cards
        { value: AFormat.A3_FORMAT, name: 'A3' } // ( 1/6 A4 ) 6 cards
    ];
    armIcon = armIcon;

    static convertToNumberOrNull(value): number {
        if (isNaN(+value) || value === '') {
            return null;
        }
        return +value;
    }

    constructor(
        private tasksServices: TaskService,
        private privilegesService: PrivilegeService,
        private activeRoute: ActivatedRoute,
        private lessonService: LessonService,
        private accountService: AccountService,
        private exExerciseService: ExExerciseService,
        private templateService: TemplateService,
        private formBuilder: FormBuilder,
        private router: Router,
        public dialog: MatDialog
    ) {
        setTheme('bs4');
    }

    ngOnInit(): void {
        this.getProfileData();
        const lessonId = this.activeRoute.snapshot.params['lesson_id'];
        const boxId = this.activeRoute.snapshot.params['box_id'];
        this.getLessonExExercise(Number(lessonId));
        this.initializeBoxEditForm();
        forkJoin(this.getShortDataLesson(lessonId), this.getBox(boxId), this.getLevels()).subscribe(() => {
            this.getCharacters();
            this.getKairosBonusSettings();
            this.getPrivilegeSettings();
            this.getSubjectFiles(this.lesson.subject).subscribe(() => {
                this.getInitialData();
                this.generatePreview();
            });
            this.fillBoxEditForm();
            this.getCharacter();
            this.checkAcces();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    private getProfileData() {
        this.accountService.getProfileData().subscribe(response => {
            if (response.data && response.data.user.username) {
                this.profile = response.data;
                this.setAccess();
                if (this.lesson) {
                    this.checkAcces();
                }
            }
        });
    }

    private checkAcces(): void {
        const includeTemplateWord = this.lesson.class_title.includes('Шаблон');
        if (includeTemplateWord) {
            if (!this.groupRights.edit_tamplates.is_access) {
                this.groupRights.copy_other_lesson.is_access = false;
                this.groupRights.delete_other_lesson.is_access = false;
                this.groupRights.edit_other_lesson.is_access = false;
            }

            if (this.groupRights.edit_tamplates.is_access) {
                this.groupRights.copy_other_lesson.is_access = true;
                this.groupRights.edit_other_lesson.is_access = true;
            }
        }

        this.isAccedEdit =
            (this.lesson.status === LESSON_CANCELED && this.groupRights.edit_other_lesson.is_access) ||
            (this.lesson.status === LESSON_CREATED && this.groupRights.edit_other_lesson.is_access);
    }

    initializeBoxEditForm(): void {
        this.boxEditForm = this.formBuilder.group({
            boxLevel: ['', Validators.compose([Validators.required])],
            boxSellCharacter: ['', []],
            boxExercisesInCard: ['', Validators.compose([Validators.required, Validators.min(1), Validators.max(10)])],
            boxCardsFormat: ['', Validators.required],
            boxCost: ['', Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])],
            boxRewardNoRisk: ['', Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])],
            boxCheckCharacter: ['', []],
            boxDescription: ['', []],
            boxComment: ['', []],
            boxRiskCharacter: ['', []],
            boxRewardWithRisk: ['', Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])],
            boxPenaltyWithRisk: ['', Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])],
            boxAdditionalAttempts: [
                '',
                Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])
            ],
            boxNumberOfCards: ['', Validators.compose([Validators.required, Validators.min(1), Validators.max(99)])],
            boxFilesCount: ['', Validators.compose([Validators.required, Validators.min(1)])],
            boxPrivileges: this.formBuilder.array([]),
            boxKairosBonus: ['', Validators.min(0)],
            boxRewardForAll1: ['0', Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])],
            boxRewardForAll2: ['0', Validators.compose([Validators.required, Validators.min(0), Validators.max(99)])]
        });
    }

    fillBoxEditForm(): void {
        const box = this.box;
        this.boxEditForm.get('boxLevel').setValue(box.level);
        this.boxEditForm.get('boxSellCharacter').setValue(box.sell_character || '');
        this.boxEditForm.get('boxExercisesInCard').setValue(box.exercises_in_card);
        this.boxEditForm.get('boxCardsFormat').setValue(box.cards_format);
        this.boxEditForm.get('boxCost').setValue(box.cost);
        this.boxEditForm.get('boxDescription').setValue(box.description);
        this.boxEditForm.get('boxComment').setValue(box.comment);
        this.boxEditForm.get('boxRewardNoRisk').setValue(box.reward_no_risk);
        this.boxEditForm.get('boxCheckCharacter').setValue(box.check_character || '');
        this.boxEditForm.get('boxRiskCharacter').setValue(box.risk_character || '');
        this.boxEditForm.get('boxRewardWithRisk').setValue(box.reward_with_risk);
        this.boxEditForm.get('boxPenaltyWithRisk').setValue(box.penalty_with_risk);
        this.boxEditForm.get('boxAdditionalAttempts').setValue(box.additional_attempts);
        this.boxEditForm.get('boxNumberOfCards').setValue(box.number_of_cards);
        this.boxEditForm.get('boxRewardForAll1').setValue(box.reward_for_all);
        this.boxEditForm.get('boxRewardForAll2').setValue(box.reward_for_all);

        this.isLoaded = true;
        this.getBoxRisk();
    }

    getInitialData(): void {
        this.isExExerciseActive = this.box.is_ex_exercise;
        this.box.files.forEach(file => {
            this.boxFilesIDs.push(file.id);

            this.subjectFiles.forEach(subjectFile => {
                if (subjectFile.file_id === file.id) {
                    this.initialSubtopics.push(subjectFile);
                }
            });
        });
        this.boxEditForm.get('boxFilesCount').setValue(this.boxFilesIDs.length);
    }

    getBox(boxId: number): any {
        return this.lessonService.getLessonBox(boxId).pipe(
            map((next: any) => {
                this.box = next;
                this.checkHabirint();
                this.checkSmartSprint();
                return next;
            })
        );
    }

    getBoxRisk(): void {
        const boxCost = this.boxEditForm.get('boxCost').value;
        const boxPenaltyWithRisk = this.boxEditForm.get('boxPenaltyWithRisk').value;
        this.withRiskBox = boxCost > 0 || boxPenaltyWithRisk > 0;
    }

    getBoxLevelById(levelID: number): BoxLevel {
        for (const level of this.levels) {
            if (levelID === level.id) {
                return level;
            }
        }
        return null;
    }

    getShortDataLesson(lessonID: number): any {
        return this.lessonService.getShortDataLesson(lessonID).pipe(
            map((next: ShortDataLesson) => {
                this.lesson = next;
                this.lesson.smart_sprints.sort((item1, item2): number => {
                    return item1.duration > item2.duration ? 1 : -1;
                });
                const epochId = Number(this.lesson.epoch);
                this.hasKairosBonus = next.has_kairos_bonus;
                this.getCurrentCurrency(epochId);
                return next;
            })
        );
    }

    getSubjectFiles(subjectTitle): any {
        return this.tasksServices.getSubjectFiles(subjectTitle).pipe(
            map((next: any) => {
                this.subjectFiles = next.files;
                return next;
            })
        );
    }

    getLevels(): any {
        return this.lessonService.getLevels().pipe(
            map((next: any) => {
                this.levels = next.results;
                return next.results;
            })
        );
    }

    chooseFileModal(box: any) {
        this.getShortDataLesson(this.lesson.id);
        if (this.isAccedEdit) {
            const dialogRef = this.dialog.open(SelectFilesComponent, {
                width: '99%',
                minWidth: '1100px',
                height: '90%',
                data: {
                    files: this.subjectFiles,
                    initialSubtopics: this.initialSubtopics,
                    level: box.level,
                    title: box.title
                }
            });

            dialogRef.afterClosed().subscribe(files => {
                if (!files) {
                    return;
                }
                this.fileSelected(files);
            });
        }
    }

    getCharacters(): any {
        const epochId = this.lesson.epoch;
        this.privilegesService.getEpochCharacters(epochId).subscribe(resp => {
            if (!resp.error) {
                this.characters = resp.data;
            }
        });
    }

    getCharacter(): void {
        this.box.sell_character = +this.boxEditForm.get('boxSellCharacter').value;
        if (this.box.sell_character) {
            this.characters.forEach(it => {
                if (it.id === this.box.sell_character) {
                    this.character = it;
                }
            });
        } else {
            this.character = null;
        }
    }

    generatePreview(): void {
        this.box.exercises_in_card = this.boxEditForm.get('boxExercisesInCard').value;
        this.box.cards_format = this.boxEditForm.get('boxCardsFormat').value;

        if (this.boxFilesIDs.length !== 0 && this.box.exercises_in_card > 0 && this.box.exercises_in_card <= 10) {
            this.templateService
                .sendTemplateRequestByFilesID(this.box.exercises_in_card, this.boxFilesIDs)
                .subscribe(resp => {
                    if (!resp.error) {
                        this.cardTemplate = resp.data;
                    }
                });
        }
    }

    fileSelected(files): void {
        this.initialSubtopics = [];
        this.boxFilesIDs = [];

        files.forEach(file => {
            this.initialSubtopics.push(file);
            this.boxFilesIDs.push(file.file_id);
        });
        this.boxEditForm.get('boxFilesCount').setValue(this.boxFilesIDs.length);
        this.generatePreview();
    }

    onLevelChange(): void {
        const boxRewardNoRiskControl = this.boxEditForm.get('boxRewardNoRisk');
        const boxLevelControl = this.boxEditForm.get('boxLevel');
        this.boxLevel = this.getBoxLevelById(+boxLevelControl.value).level;

        if (boxRewardNoRiskControl.untouched) {
            boxRewardNoRiskControl.setValue(this.boxLevel);
        }
        this.box.level = boxLevelControl.value;
    }

    onBoxSave(): void {
        const boxFiles = this.boxFilesIDs;
        const form = this.boxEditForm;
        const id = this.box.id;
        const title = Number(this.box.title);
        const lesson = this.box.lesson;
        const generated = this.box.generated;
        const level = +form.get('boxLevel').value;
        const numberOfCards = +form.get('boxNumberOfCards').value;
        const exercisesInCard = +form.get('boxExercisesInCard').value;
        const cost = +form.get('boxCost').value;
        const rewardNoRisk = +form.get('boxRewardNoRisk').value;
        const rewardForAll = !this.isSmartSprintOpen
            ? +form.get('boxRewardForAll1').value
            : +form.get('boxRewardForAll2').value;
        const rewardWithRisk = +form.get('boxRewardWithRisk').value;
        const penaltyWithRisk = +form.get('boxPenaltyWithRisk').value;
        const additionalAttempts = +form.get('boxAdditionalAttempts').value;
        const description = form.get('boxDescription').value;
        const comment = form.get('boxComment').value;
        const checkCharacter = BoxEditComponent.convertToNumberOrNull(form.get('boxCheckCharacter').value);
        const riskCharacter = BoxEditComponent.convertToNumberOrNull(form.get('boxRiskCharacter').value);
        const sellCharacter = BoxEditComponent.convertToNumberOrNull(form.get('boxSellCharacter').value);
        const cardsFormat = form.get('boxCardsFormat').value;
        const costNoRisk = 0;
        const costWithRisk = 0;
        const privileges = this.activatedPrivileges.map(privilege => privilege.id);
        let kairosBonus = form.get('boxKairosBonus').value || '';
        kairosBonus = kairosBonus !== '' ? Number(kairosBonus) : 0;

        const updatedBox = <MinifiedBox>{
            id: id,
            title: title,
            level: level,
            number_of_cards: numberOfCards,
            exercises_in_card: exercisesInCard,
            lesson_id: lesson,
            cost: cost,
            files: boxFiles,
            cost_no_risk: costNoRisk,
            reward_no_risk: rewardNoRisk,
            cost_with_risk: costWithRisk,
            reward_with_risk: rewardWithRisk,
            penalty_with_risk: penaltyWithRisk,
            additional_attempts: additionalAttempts,
            check_character_id: checkCharacter,
            risk_character_id: riskCharacter,
            sell_character_id: sellCharacter,
            cards_format: cardsFormat,
            generated: generated,
            privileges: privileges,
            kairos_bonus: kairosBonus,
            is_ex_exercise: this.isExExerciseActive,
            description: description,
            comment: comment,
            smart_sprints: this.getActivedSmartSprints(),
            reward_for_all: rewardForAll,
            is_habirint: this.isHabirintBox
        };

        this.lessonService.updateLessonBox(updatedBox).subscribe(() => {
            this.backToEditPage();
        });
    }

    readyForGenerate(): boolean {
        return this.box.exercises_in_card !== 0 && this.boxFilesIDs.length !== 0 && this.box.number_of_cards !== 0;
    }

    generateBox(): void {
        if (confirm('Вы действительно хотите перегенирировать варианты?')) {
            this.lessonService.generateBox(this.box.id).subscribe(
                () => {
                    this.backToEditPage();
                },
                error => {
                    alert(`Произошла ошибка при попытке перегенерации! ${error.message}`);
                }
            );
        }
    }

    onCancel(): void {
        window.history.back();
    }

    private backToEditPage() {
        this.router.navigate(['/teacher/prepare', this.box.lesson, 'edit']);
    }

    private getPrivilegeSettings(): void {
        this.privilegesService.getLessonPrivilegeSettings(this.lesson.id).subscribe(response => {
            this.privilegeSettings = response.data;
            this.fillActivatedPrivileges();
        });
    }

    private fillActivatedPrivileges(): void {
        this.privilegeSettings.forEach(setting => {
            const index = setting.boxes.findIndex(boxID => boxID === this.box.id);
            if (index !== -1) {
                this.activatedPrivileges.push(setting);
            }
        });
    }

    public togglePrivilegeSetting(selectedSetting): void {
        const index = this.activatedPrivileges.findIndex(setting => selectedSetting.id === setting.id);
        if (index !== -1) {
            this.activatedPrivileges.splice(index, 1);
        } else {
            this.activatedPrivileges.push(selectedSetting);
        }
    }

    public isActivePrivilege(privilegeSetting): boolean {
        if (!this.activatedPrivileges) {
            return false;
        }
        return this.activatedPrivileges.findIndex(setting => privilegeSetting.id === setting.id) !== -1;
    }

    toggleKairosBonus(): void {
        this.isKairosBonusActivated = !this.isKairosBonusActivated;
        if (!this.isKairosBonusActivated) {
            this.boxEditForm.get('boxKairosBonus').setValue(0);
        } else {
            this.handleKairosBonus(true);
        }
    }

    getKairosBonusSettings(): void {
        this.privilegesService.getKairosBonusSettings(this.box.id).subscribe(resp => {
            if (resp.data) {
                this.kairosBonusSettings = resp.data as KairosBonusSettings;
                this.getKairosBonus();
            }
        });
    }

    private getKairosBonus() {
        this.privilegesService.getKairosBonus(this.box.id).subscribe(resp => {
            this.kairosBonus = resp.data as KairosBonus;
            this.handleKairosBonus();
        });
    }

    private handleKairosBonus(isBonusToggled = false): void {
        let bonusValue = 0;
        if (this.kairosBonus && !isBonusToggled) {
            bonusValue = this.kairosBonus.bonus;
        } else if (isBonusToggled) {
            const reward = +this.boxEditForm.get('boxRewardNoRisk').value;
            const fullReward = reward * this.kairosBonusSettings.bonus_multiplier;
            bonusValue = fullReward - reward;
        } else {
            this.isKairosBonusActivated = false;
        }
        this.boxEditForm.get('boxKairosBonus').setValue(bonusValue);
    }

    private getLessonExExercise(lessonId: number) {
        this.exExerciseService.getLessonExExercise(lessonId).subscribe(resp => {
            this.exExercise = resp;
        });
    }

    private checkSmartSprint() {
        if (this.box && this.box.smart_sprints.length) {
            this.boxLevel = Number(this.box.level);
            this.isSmartSprintOpen = true;
            if (this.boxLevel) {
                this.lesson.smart_sprints.forEach(item => {
                    this.box.smart_sprints.forEach(boxItem => {
                        if (boxItem.duration === item.duration) {
                            item.successful = boxItem.successful;
                            const smartSprintItem = cloneDeep(item);
                            this.activedSmartSprints.push(smartSprintItem);
                        }
                    });
                });
            }
        }
    }

    private getCurrentCurrency(epochId: number) {
        this.tasksServices.getEpochCurrency(epochId).subscribe(resp => {
            if (resp.results) {
                this.currentCurrency = resp.results[0];
            }
        });
    }

    changeGameConditions(event) {
        if (event === '1') {
            this.isSmartSprintOpen = true;
            return;
        }
        this.isSmartSprintOpen = false;
    }

    toggleSmartSprintItem(smartSprintItem: SmartSprint) {
        const index = this.activedSmartSprints.findIndex(item => smartSprintItem.duration === item.duration);

        if (index !== -1) {
            this.activedSmartSprints.splice(index, 1);
        } else {
            this.activedSmartSprints.push(smartSprintItem);
        }
    }

    isActiveSmartSprint(smartSprintItem: SmartSprint) {
        const isActivedSmartSprintsExists = this.activedSmartSprints.some(
            item => item.duration === smartSprintItem.duration
        );
        return isActivedSmartSprintsExists;
    }

    private getActivedSmartSprints() {
        this.activedSmartSprints.forEach(item => {
            const instanceSprint = this.lesson.smart_sprints.filter(it => it.id === item.id)[0];
            item.reward = [0, 9].includes(this.boxLevel)
                ? instanceSprint.reward
                : instanceSprint.reward * this.boxLevel;
        });
        return this.activedSmartSprints;
    }

    getTimeSuccess(smartSprintItem: SmartSprint) {
        if (smartSprintItem.successful) {
            return 'Зачет';
        }
        return 'Не зачет';
    }

    toggleTimeSuccessedSprint(smartSprintItem: SmartSprint) {
        this.activedSmartSprints.filter(item => {
            if (item.duration === smartSprintItem.duration) {
                if (smartSprintItem.successful) {
                    smartSprintItem.successful = false;
                    item.successful = false;
                    return;
                }
                smartSprintItem.successful = true;
                item.successful = true;
            }
        });
    }

    private checkHabirint(): void {
        if (this.lesson && this.lesson.is_habirint && this.box.is_habirint) {
            this.isHabirintBox = true;
        }
    }

    private setAccess(): void {
        const user_rights = this.profile.group.rights;

        user_rights.forEach(item => {
            switch (+item.index_title) {
                // доступ к изменению статуса урока
                case this.groupRights.change_status_other_lesson.index_title:
                    this.groupRights.change_status_other_lesson.is_access = true;
                    break;
                // доступ к копированию чужих уроков
                case this.groupRights.copy_other_lesson.index_title:
                    this.groupRights.copy_other_lesson.is_access = true;
                    break;
                // доступ к созданию урока
                case this.groupRights.create_lesson.index_title:
                    this.groupRights.create_lesson.is_access = true;
                    break;
                // доступ к удалению чужого урока
                case this.groupRights.delete_other_lesson.index_title:
                    this.groupRights.delete_other_lesson.is_access = true;
                    break;
                // доступ к редактированию чужого урока
                case this.groupRights.edit_other_lesson.index_title:
                    this.groupRights.edit_other_lesson.is_access = true;
                    break;
                // доступ к редактированию шаблонов
                case this.groupRights.edit_tamplates.index_title:
                    this.groupRights.edit_tamplates.is_access = true;
                    break;
                default:
                    break;
            }
        });
    }
}
